import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from "firebase/firestore";
import JSZip from "jszip";
import { db } from "../firebase/config";
import AIService from "./AIService";
import type {
  CreateSplashDTO,
  GeneratedSplashScreen,
  GeneratedSplashVariant,
  SplashCropSettings,
} from "../types/splash";
import { buildAppContextLines } from "../utils/appContextPrompt";

const SPLASH_COLLECTION = "appSplashScreens";

class SplashService {
  private sanitizeColorScheme(colorScheme: {
    primary?: string;
    secondary?: string;
    accent?: string;
  }): {
    primary?: string;
    secondary?: string;
    accent?: string;
  } {
    const next: { primary?: string; secondary?: string; accent?: string } = {};
    if (colorScheme.primary !== undefined) next.primary = colorScheme.primary;
    if (colorScheme.secondary !== undefined) {
      next.secondary = colorScheme.secondary;
    }
    if (colorScheme.accent !== undefined) next.accent = colorScheme.accent;
    return next;
  }

  private generateSplashId(): string {
    return `splash_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }

  private convertTimestampToDate(timestamp: unknown): Date {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    if (typeof timestamp === "string" || typeof timestamp === "number") {
      return new Date(timestamp);
    }
    return new Date();
  }

  async getSplashScreensByAppId(
    appId: string,
    userId: string,
  ): Promise<GeneratedSplashScreen[]> {
    const splashRef = collection(db, SPLASH_COLLECTION);
    const q = query(
      splashRef,
      where("appId", "==", appId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((splashDoc) => {
      const data = splashDoc.data();
      return {
        splashId: data.splashId,
        appId: data.appId,
        userId: data.userId,
        lightImageUrl: data.lightImageUrl,
        darkImageUrl: data.darkImageUrl,
        lightSourceAssetId: data.lightSourceAssetId,
        darkSourceAssetId: data.darkSourceAssetId,
        lightSourceStoragePath: data.lightSourceStoragePath,
        darkSourceStoragePath: data.darkSourceStoragePath,
        prompt: data.prompt,
        style: data.style,
        mood: data.mood,
        colorScheme: data.colorScheme || {},
        metadata: {
          generatedAt: this.convertTimestampToDate(data.metadata?.generatedAt),
          aiModel: data.metadata?.aiModel || "",
          provider: data.metadata?.provider || "gemini",
        },
      } as GeneratedSplashScreen;
    });
  }

  async generateSplashScreenPair(
    userId: string,
    splashRequest: CreateSplashDTO,
  ): Promise<GeneratedSplashScreen> {
    const prompt = this.buildPrompt(splashRequest);
    const lightPrompt = `${prompt}\nTheme variant: light mode splash screen.`;
    const darkPrompt = `${prompt}\nTheme variant: dark mode splash screen with darker background while preserving visual identity.`;

    const [lightResult, darkResult] = await Promise.all([
      AIService.generateImage(lightPrompt, {
        appId: splashRequest.appId,
        assetCategory: "splash-screen-light",
        model: splashRequest.model,
        aspectRatio: "1:1",
        size: "1024x1024",
        n: 1,
      }),
      AIService.generateImage(darkPrompt, {
        appId: splashRequest.appId,
        assetCategory: "splash-screen-dark",
        model: splashRequest.model,
        aspectRatio: "1:1",
        size: "1024x1024",
        n: 1,
      }),
    ]);

    const lightImage = lightResult.images[0];
    const darkImage = darkResult.images[0];

    if (!lightImage || !darkImage) {
      throw new Error("Failed to generate both light and dark splash images.");
    }

    const splashId = this.generateSplashId();
    const newSplash = {
      splashId,
      appId: splashRequest.appId,
      userId,
      lightImageUrl: lightImage.downloadUrl,
      darkImageUrl: darkImage.downloadUrl,
      ...(lightImage.assetId ? { lightSourceAssetId: lightImage.assetId } : {}),
      ...(darkImage.assetId ? { darkSourceAssetId: darkImage.assetId } : {}),
      ...(lightImage.storagePath
        ? { lightSourceStoragePath: lightImage.storagePath }
        : {}),
      ...(darkImage.storagePath
        ? { darkSourceStoragePath: darkImage.storagePath }
        : {}),
      prompt,
      style: splashRequest.style,
      mood: splashRequest.mood,
      colorScheme: this.sanitizeColorScheme(splashRequest.colorScheme),
      metadata: {
        generatedAt: serverTimestamp(),
        aiModel: lightResult.model || darkResult.model,
        provider: lightResult.provider || darkResult.provider,
      },
    };

    await addDoc(collection(db, SPLASH_COLLECTION), newSplash);

    return {
      ...newSplash,
      metadata: {
        generatedAt: new Date(),
        aiModel: lightResult.model || darkResult.model,
        provider: lightResult.provider || darkResult.provider,
      },
    } as GeneratedSplashScreen;
  }

  async generateSplashVariants(
    splash: GeneratedSplashScreen,
    crop: SplashCropSettings,
  ): Promise<GeneratedSplashVariant[]> {
    if (!splash.lightSourceStoragePath?.trim()) {
      throw new Error("Selected splash record is missing light source image.");
    }
    if (!splash.darkSourceStoragePath?.trim()) {
      throw new Error("Selected splash record is missing dark source image.");
    }

    const result = await AIService.generateSplashVariants({
      appId: splash.appId,
      sourceStoragePath: splash.lightSourceStoragePath,
      darkSourceStoragePath: splash.darkSourceStoragePath,
      crop,
    });

    return (result.variants || []).map((variant) => ({
      ...variant,
      dataUrl: `data:${variant.mimeType || "image/png"};base64,${variant.base64}`,
    }));
  }

  async downloadVariants(
    variants: GeneratedSplashVariant[],
    appName: string,
  ): Promise<void> {
    const zip = new JSZip();
    const safeAppName = appName.replace(/[^a-zA-Z0-9-_]/g, "_");

    for (const variant of variants) {
      if (!variant.base64) continue;
      zip.file(variant.fileName, variant.base64, { base64: true });
    }

    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeAppName}-capacitor-splash-assets.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async deleteSplashScreen(splashId: string, userId: string): Promise<void> {
    const q = query(
      collection(db, SPLASH_COLLECTION),
      where("splashId", "==", splashId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw new Error("Splash screen not found");
    }

    await deleteDoc(querySnapshot.docs[0].ref);
  }

  private buildPrompt(request: CreateSplashDTO): string {
    const hasPrimary = Boolean(request.colorScheme.primary?.trim());
    const hasSecondary = Boolean(request.colorScheme.secondary?.trim());
    const colors =
      hasPrimary || hasSecondary
        ? [
            hasPrimary ? `primary ${request.colorScheme.primary}` : "",
            hasSecondary ? `secondary ${request.colorScheme.secondary}` : "",
            request.colorScheme.accent?.trim()
              ? `accent ${request.colorScheme.accent}`
              : "",
          ]
            .filter(Boolean)
            .join(", ")
        : "no fixed palette specified";

    const lines = [
      `Generate a mobile app splash screen for "${request.appName}".`,
      "Target output: Capacitor splash source image (square 2732x2732 after resizing).",
      request.styleMode === "ai"
        ? `AI style direction: ${
            request.aiStylePrompt ||
            "Use premium cinematic splash art direction."
          }`
        : `Style: ${request.style}. Mood: ${request.mood}.`,
      `Colors: ${colors}.`,
      ...buildAppContextLines({
        name: request.appName,
        description: request.appDescription,
        category: request.appCategory,
        platform: request.appPlatform,
        storeUrl: request.appStoreUrl,
        androidStoreUrl: request.androidStoreUrl,
        iosStoreUrl: request.iosStoreUrl,
        researchContext: request.researchContext,
      }),
      `Keywords: ${request.keywords?.join(", ") || "N/A"}.`,
      `Avoid elements: ${request.avoidElements?.join(", ") || "N/A"}.`,
      request.styleMode === "ai"
        ? `AI selected style/mood anchor: style=${
            request.aiSuggestedStyle || "minimalist"
          }, mood=${request.aiSuggestedMood || "professional"}.`
        : `Extra direction: ${request.customPrompt?.trim() || "N/A"}.`,
      "Composition constraints: one dominant focal subject/logo mark, balanced centered layout, and strong visual hierarchy.",
      "Safe area constraints: keep all essential content within the center 60% so it survives platform crop behavior.",
      "Text prohibition: do not include any text overlays, letters, words, taglines, captions, numbers, UI labels, or logo wordmarks anywhere in the artwork.",
      "Typography exclusion: image must be purely visual with symbols/shapes/illustration only; reject any typographic or pseudo-typographic marks.",
      "Quality constraints: high contrast, clean edges, no watermark, no compression artifacts, no clutter.",
      "Output constraints: full square artwork with hard square corners and no rounded masks baked into the image.",
    ];

    return lines.join("\n");
  }
}

export default new SplashService();
